import { Flex, Grid, Tag, Typography } from "antd/lib";
import styles from "./Events.module.scss";
import React from "react";
import { useTranslation } from "react-i18next";
import Link from "next/link";
import { Event, Report } from "@/types/events";
import {
  editReportUrl,
  myEventDetailsUrl,
  myEventMaterialsUrl,
  publicEventDetailsUrl,
  reportMyEventUrl,
} from "@/utils/appUrls";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useAuth } from "@/context/AuthContext";
import { v4 as uuidv4 } from "uuid";
import { getEventTypeTagText, getFormattedManualLocation } from "@/utils/helpers";
import { CAME_FROM_LISTING_KEY } from "@/constants/common";
import { safeSessionStorage } from "@/utils/storage";

const { Text, Title } = Typography;

interface EventsCardProps {
  isMyEvents?: boolean;
  isInReportingContext?: boolean;
  materialsListButton?: boolean;
  event?: Event;
  report?: Report;
  detailsBtnRef?: React.RefObject<HTMLAnchorElement | null>;
}

const { useBreakpoint } = Grid;

dayjs.extend(utc);

const EventCard: React.FC<EventsCardProps> = ({
  isMyEvents,
  isInReportingContext,
  materialsListButton = false,
  event,
  report,
  detailsBtnRef,
}) => {
  const { t } = useTranslation();
  const id = event?.uuid ?? report?.event_uuid ?? uuidv4();
  const viewDetailsLink = isMyEvents ? myEventDetailsUrl(id) : publicEventDetailsUrl(id);

  const markNavigationFromListing = () => {
    safeSessionStorage.setItem(CAME_FROM_LISTING_KEY, "1");
  };
  const viewEventMaterials = myEventMaterialsUrl(id);
  const screens = useBreakpoint();
  const { userIsRetoolingFacilitator } = useAuth();
  const locationPlace = event?.location_place ?? report?.location_place;
  const manualLocation = event?.manual_location ?? report?.manual_location;
  const hasPlace = !!(locationPlace || manualLocation);

  const mainFacilitator = event?.main_facilitator ?? report?.main_facilitator;
  const assistantFacilitators = event?.assistant_facilitators ?? report?.assistant_facilitators;
  const showFacilitators = isMyEvents !== false;
  const hostOrganizations = event?.host_organizations ?? report?.host_organizations;
  const mainLanguage = event?.main_language;
  const material = event?.material ?? report?.material;
  const hostCount = hostOrganizations ? hostOrganizations.split(",").filter((part) => part.trim()).length : 0;

  const getLocation = () => {
    let location = locationPlace;

    if (manualLocation) {
      if (event) location = getFormattedManualLocation(event);
      if (report) location = getFormattedManualLocation(report);
    }

    return location;
  };

  return (
    <Flex vertical justify="space-between" gap={25} className={styles.eventCard}>
      <Flex justify="space-between" gap={16} vertical={!screens.sm}>
        <Flex vertical justify="space-between" gap={16}>
          <Text>
            {dayjs.utc(event?.start_date ?? report?.start_date).format("D MMMM YYYY")} -{" "}
            {dayjs.utc(event?.end_date ?? report?.end_date).format("D MMMM YYYY")}
            {!!report?.report_id && <Text strong> ({report.report_id})</Text>}
          </Text>
          {!!event?.schedule_tips && <Text>{event.schedule_tips}</Text>}
          {!!(event?.title || report?.event_title) && (
            <Title level={5} style={{ margin: 0 }}>
              {event?.title ?? report?.event_title}
            </Title>
          )}
          <Flex vertical gap={2}>
            {showFacilitators && !!mainFacilitator && (
              <Text>
                {t("myReportingPage.listing.mainFacilitator")}: <Text strong> {mainFacilitator}</Text>
              </Text>
            )}
            {showFacilitators && !!assistantFacilitators && (
              <Text>
                {t("myReportingPage.listing.assistantFacilitators")}: <Text strong> {assistantFacilitators}</Text>
              </Text>
            )}
            {!!hostOrganizations && (
              <Text>
                {t(hostCount > 1 ? "myReportingPage.listing.hosts" : "myReportingPage.listing.host")}:{" "}
                <Text strong> {hostOrganizations}</Text>
              </Text>
            )}
            {!!mainLanguage && (
              <Text>
                {t("myReportingPage.listing.language")}: <Text strong> {mainLanguage}</Text>
              </Text>
            )}
            {!!material && (
              <Text>
                {t("myReportingPage.listing.material")}: <Text strong> {material}</Text>
              </Text>
            )}
          </Flex>
        </Flex>
        <Flex vertical justify="space-between" align={screens.sm ? "flex-end" : "flex-start"}>
          <Flex vertical gap={8} align={screens.sm ? "flex-end" : "flex-start"}>
            {!!(event?.event_type || report?.report_type) && (
              <Tag variant="filled" color="cyan">
                {event?.event_type
                  ? getEventTypeTagText(t, event.event_type, event.equipping_level)
                  : report && getEventTypeTagText(t, report.report_type, report.equipping_level)}
              </Tag>
            )}
            <Tag variant="filled" color="green">
              {event?.online || report?.online ? t("eventsPage.tags.online") : t("eventsPage.tags.inPerson")}
            </Tag>
            {isMyEvents && (!!event || report?.event_public !== null) && (
              <Tag variant="filled" color="green">
                {event?.public || report?.event_public ? t("eventsPage.tags.public") : t("eventsPage.tags.private")}
              </Tag>
            )}
          </Flex>
        </Flex>
      </Flex>
      <Flex justify="space-between" align={screens.sm ? "center" : "stretch"} gap={16} vertical={!screens.sm}>
        <Flex gap={screens.sm ? 48 : 16} style={{ display: "flex" }} vertical={!screens.sm}>
          {hasPlace && (
            <Flex gap={8} align="center">
              <Text style={{ borderRadius: "50%", backgroundColor: "#D9D9D9" }}>
                <i className={`${styles.cardIcon} icon-world_stroke`} />
              </Text>
              <Text strong>{getLocation()}</Text>
            </Flex>
          )}
          {isMyEvents && (!event?.has_confidential_participants || report?.event_has_confidential_participants) && (
            <Flex gap={8} align="center" className={styles.eventPeople}>
              <Text style={{ borderRadius: "50%", backgroundColor: "#D9D9D9" }}>
                <i className={`${styles.cardIcon} icon-user_stroke`} />
              </Text>
              <Flex gap={4}>
                <Text>{t("eventsPage.people")}: </Text>
                <Text strong>{event?.participants_count ?? report?.participants_count}</Text>
              </Flex>
            </Flex>
          )}
        </Flex>
        <Flex gap={16} className={styles.eventLink}>
          {materialsListButton && !!(event?.material || report?.material) && (
            <Link
              href={viewEventMaterials}
              onClick={markNavigationFromListing}
              className="buttonLink buttonLink_secondary"
            >
              {t("myProgramsPage.buttons.viewMaterials")}
            </Link>
          )}
          {isInReportingContext && !!event && !event.reported && !userIsRetoolingFacilitator ? (
            <Link
              href={reportMyEventUrl(event.uuid)}
              onClick={markNavigationFromListing}
              className={`buttonLink buttonLink_secondary ${screens.sm ? "" : "w-full"}`}
            >
              {t("myReportingPage.buttons.createReport")}
            </Link>
          ) : isInReportingContext && !!report && !!report.event_uuid && !userIsRetoolingFacilitator ? (
            <>
              {!event?.is_report && !report?.event_is_report && (
                <Link
                  href={viewDetailsLink}
                  onClick={markNavigationFromListing}
                  className={`buttonLink buttonLink_secondary ${screens.sm ? "" : "w-full"}`}
                >
                  {t("myReportingPage.buttons.viewEvent")}
                </Link>
              )}
              <Link
                href={editReportUrl(report.event_uuid, report.report_type)}
                onClick={markNavigationFromListing}
                className={`buttonLink buttonLink_secondary ${screens.sm ? "" : "w-full"}`}
              >
                {t("myReportingPage.buttons.editReport")}
              </Link>
            </>
          ) : event?.is_report || report?.event_is_report ? (
            <></>
          ) : (
            <Link
              href={viewDetailsLink}
              ref={detailsBtnRef}
              onClick={markNavigationFromListing}
              className={`buttonLink buttonLink_secondary ${screens.sm || materialsListButton ? "" : "w-full"}`}
            >
              {isInReportingContext ? t("myReportingPage.buttons.viewEvent") : t("eventsPage.buttons.viewDetails")}
            </Link>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default EventCard;
